import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalState, useOnline } from '@ndrc/app-sdk/client';
import { useDesk } from '../desk/context';
import { callCore, mutateCore } from '../lib/rpc';
import { t } from '../messages';
import './projection.css';
import './submissions.css';
interface Review {
  draftNote?: string;
  publicNote: string | null;
  revision?: number;
  publishedAt: string | null;
}
interface Submission {
  id: string;
  title: string;
  version: number;
  createdAt: string;
  userName: string;
  activityTitle: string | null;
}
interface ReviewHistoryItem {
  id: string;
  revision: number | null;
  note: string;
  published: boolean;
  authorName: string | null;
  imported: boolean;
  createdAt: string;
}
interface Selected extends Submission {
  history: { total: number; items: ReviewHistoryItem[] };
  record: {
    title: string;
    status: string;
    fields: { key: string; label: string; value: string }[];
  };
  justification: string;
  sourceObservedAt: string;
  review: Review | null;
}
interface Result {
  items: Submission[];
  total: number;
  selected: Selected | null;
}
function date(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(Date.parse(value));
}
function ReviewForm({ submissionId, review }: { submissionId: string; review: Review | null }) {
  const { me, observing } = useDesk(),
    online = useOnline(),
    cache = useQueryClient();
  const [draft, setDraft, stored] = useLocalState(`external-review:${me.id}:${submissionId}`, {
    note: review?.draftNote ?? '',
    revision: review?.revision ?? 0,
    requestKey: crypto.randomUUID(),
    publish: false,
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const stale = draft.revision !== (review?.revision ?? 0);
  async function save(publish: boolean) {
    if (busy || !online || observing) return;
    const pending = {
      ...draft,
      publish,
      requestKey: draft.publish === publish ? draft.requestKey : crypto.randomUUID(),
    };
    setDraft(pending);
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await mutateCore<{ revision: number }>(
        'external.review',
        {
          submissionId,
          revision: pending.revision,
          note: pending.note,
          publish,
          requestKey: pending.requestKey,
        },
        pending.requestKey,
      );
      setDraft({ ...pending, revision: result.revision, requestKey: crypto.randomUUID() });
      setNotice(t(publish ? 'cms.submission.reviewPublished' : 'cms.submission.reviewSaved'));
      await cache.invalidateQueries({ queryKey: ['core', 'external.submissions'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('cms.submission.error'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="cms-submission-form">
      <h4>{t('cms.submission.reviewTitle')}</h4>
      <p>{t('cms.submission.reviewHelp')}</p>
      <label>
        {t('cms.submission.draftNote')}
        <textarea
          rows={3}
          maxLength={6000}
          value={draft.note}
          disabled={busy || Boolean(observing)}
          onChange={(e) =>
            setDraft({ ...draft, note: e.target.value, requestKey: crypto.randomUUID() })
          }
        />
      </label>
      {stale && (
        <p role="alert">
          {t('cms.submission.reviewConflict')}{' '}
          <button
            type="button"
            className="ndrc-button"
            onClick={() =>
              setDraft({
                note: review?.draftNote ?? '',
                revision: review?.revision ?? 0,
                requestKey: crypto.randomUUID(),
                publish: false,
              })
            }
          >
            {t('cms.submission.reloadReview')}
          </button>
        </p>
      )}
      {!stored && <p role="alert">{t('cms.submission.localError')}</p>}
      {error && (
        <p role="alert" className="ndrc-error">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <div className="cms-projection-toolbar">
        <button
          type="button"
          className="ndrc-button"
          disabled={busy || !online || Boolean(observing) || !draft.note.trim()}
          onClick={() => {
            void save(false);
          }}
        >
          {t('cms.submission.saveReview')}
        </button>
        <button
          type="button"
          className="ndrc-button"
          disabled={busy || !online || Boolean(observing) || !draft.note.trim()}
          onClick={() => {
            void save(true);
          }}
        >
          {t('cms.submission.publishReview')}
        </button>
      </div>
    </div>
  );
}
export function SubmissionsPanel({
  classId,
  environmentId,
}: {
  classId?: string;
  environmentId?: string;
}) {
  const { me } = useDesk();
  const [open, setOpen] = useState(false),
    [offset, setOffset] = useState(0),
    [selectedId, setSelectedId] = useState<string>(),
    [historyOffset, setHistoryOffset] = useState(0);
  const query = useQuery({
    queryKey: [
      'core',
      'external.submissions',
      me.id,
      classId,
      environmentId,
      offset,
      selectedId,
      historyOffset,
    ],
    enabled: open,
    placeholderData: keepPreviousData,
    queryFn: () =>
      callCore<Result>('external.submissions', {
        classId,
        environmentId,
        offset,
        selectedId,
        historyOffset,
      }),
    refetchInterval: open ? 5000 : false,
  });
  const result = query.data,
    selected = result?.selected;
  return (
    <details
      className="cms-projection cms-submissions"
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary>
        {t(me.role === 'teacher' ? 'cms.submission.teacherTitle' : 'cms.submission.title')}
      </summary>
      {open && (
        <div className="cms-projection-body">
          <p className="cms-projection-help">{t('cms.submission.historyHelp')}</p>
          <div>
            <button
              type="button"
              className="ndrc-button"
              disabled={query.isFetching}
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('cms.refresh')}
            </button>
          </div>
          {query.isError && (
            <p role="alert" className="ndrc-error">
              {query.error.message}
            </p>
          )}
          {query.isPending ? (
            <p role="status">{t('cms.loading')}</p>
          ) : !result?.total ? (
            <p>{t('cms.submission.empty')}</p>
          ) : (
            <div className="cms-projection-grid">
              <div>
                <ul className="cms-projection-records">
                  {result.items.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        aria-pressed={selectedId === item.id}
                        onClick={() => {
                          setSelectedId(item.id);
                          setHistoryOffset(0);
                        }}
                      >
                        <strong>{item.title || t('cms.projection.untitled')}</strong>
                        <span>
                          {t('cms.submission.item', {
                            name: item.userName,
                            version: item.version,
                            date: date(item.createdAt),
                          })}
                        </span>
                        {item.activityTitle && <span>{item.activityTitle}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
                <nav className="cms-projection-pages" aria-label={t('cms.submission.pages')}>
                  <button
                    type="button"
                    className="ndrc-button"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - 20))}
                  >
                    {t('cms.projection.previous')}
                  </button>
                  <span>{t('cms.projection.page', { page: Math.floor(offset / 20) + 1 })}</span>
                  <button
                    type="button"
                    className="ndrc-button"
                    disabled={offset + 20 >= result.total}
                    onClick={() => setOffset(offset + 20)}
                  >
                    {t('cms.projection.next')}
                  </button>
                </nav>
              </div>
              <section className="cms-projection-detail" aria-label={t('cms.submission.detail')}>
                {selected ? (
                  <>
                    <h4>{selected.record.title || t('cms.projection.untitled')}</h4>
                    <p className="cms-projection-help">
                      {t('cms.submission.frozen', {
                        version: selected.version,
                        date: date(selected.createdAt),
                        sourceDate: date(selected.sourceObservedAt),
                      })}
                    </p>
                    <div
                      className="cms-submission-content"
                      role="region"
                      aria-label={t('cms.submission.content')}
                      tabIndex={0}
                    >
                      <dl>
                        <div>
                          <dt>{t('cms.submission.justification')}</dt>
                          <dd>{selected.justification}</dd>
                        </div>
                        {selected.record.fields.map((field) => (
                          <div key={field.key}>
                            <dt>{field.label}</dt>
                            <dd>{field.value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                    <div className="cms-submission-public">
                      <h4>{t('cms.submission.publicNote')}</h4>
                      <p>{selected.review?.publicNote || t('cms.submission.noPublicNote')}</p>
                      {selected.review?.publishedAt && (
                        <small>{date(selected.review.publishedAt)}</small>
                      )}
                    </div>
                    <details className="cms-review-history">
                      <summary>{t('cms.submission.historyTitle')}</summary>
                      <p className="cms-projection-help">
                        {t('cms.submission.historyCount', { count: selected.history.total })}
                      </p>
                      <ol tabIndex={0} aria-label={t('cms.submission.historyEntries')}>
                        {selected.history.items.map((entry) => (
                          <li key={entry.id}>
                            <header>
                              <strong>
                                {t(
                                  entry.published
                                    ? 'cms.submission.historyPublished'
                                    : 'cms.submission.historyDraft',
                                )}
                              </strong>
                              <span>{date(entry.createdAt)}</span>
                            </header>
                            <p className="cms-projection-help">
                              {entry.authorName ?? t('cms.submission.unknownAuthor')}
                            </p>
                            {entry.imported && (
                              <p className="cms-projection-help">
                                {t('cms.submission.historyImported')}
                              </p>
                            )}
                            <p className="cms-review-history-note">{entry.note}</p>
                          </li>
                        ))}
                      </ol>
                      <nav
                        className="cms-projection-pages"
                        aria-label={t('cms.submission.historyPages')}
                      >
                        <button
                          type="button"
                          className="ndrc-button"
                          disabled={historyOffset === 0 || query.isFetching}
                          onClick={() => setHistoryOffset(Math.max(0, historyOffset - 20))}
                        >
                          {t('cms.projection.previous')}
                        </button>
                        <span>
                          {t('cms.projection.page', { page: Math.floor(historyOffset / 20) + 1 })}
                        </span>
                        <button
                          type="button"
                          className="ndrc-button"
                          disabled={
                            historyOffset + 20 >= selected.history.total || query.isFetching
                          }
                          onClick={() => setHistoryOffset(historyOffset + 20)}
                        >
                          {t('cms.projection.next')}
                        </button>
                      </nav>
                    </details>
                    {me.role === 'teacher' && (
                      <ReviewForm
                        key={`${me.id}:${selected.id}`}
                        submissionId={selected.id}
                        review={selected.review}
                      />
                    )}
                  </>
                ) : (
                  <p>{t('cms.submission.choose')}</p>
                )}
              </section>
            </div>
          )}
        </div>
      )}
    </details>
  );
}
